import { IconButton, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { useStore } from "utils/useStore";
import AgentStore from "../Stores/AgentStore";
import { observer } from "mobx-react";
import {
    ArrowForward,
    ChevronLeft,
    KeyboardReturn,
} from "@mui/icons-material";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { ApplicationFiltersService } from "services/ApplicationFiltersService";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";

export const AnalysisGoalHeader = observer(() => {
    const agentStore = useStore(AgentStore);
    const messageStore = useStore(MessageStore);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState("");

    const data = agentStore.currentChatStore.chat.analysisMetadata;
    if (!data) return null;
    const analysisGoal = data.subscription?.kboAnalysisPrompt ?? data.goal;
    const isNoLongerSubscribed = !data.subscription;

    const startEditing = () => {
        setEditValue(analysisGoal);
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setIsEditing(false);
    };

    const saveEdit = async () => {
        const subscription = data.subscription;
        if (!subscription) return;

        const previousPrompt = subscription.kboAnalysisPrompt;
        // Optimistic update
        subscription.kboAnalysisPrompt = editValue;
        setIsEditing(false);

        try {
            const service = new ApplicationFiltersService();
            await service.updateApplicationFilterSubscription(subscription.id, {
                cadence: subscription.cadence,
                isKboAnalysisEnabled: subscription.isKboAnalysisEnabled,
                kboAnalysisPrompt: editValue,
            });
        } catch {
            // Revert on failure
            subscription.kboAnalysisPrompt = previousPrompt;
            messageStore.logError("Failed to update analysis prompt.");
        }
    };

    const handleKeyDown = (
        evt: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        if (evt.key === "Escape") {
            cancelEditing();
        } else if (evt.key === "Enter" && !evt.shiftKey) {
            evt.preventDefault();
            saveEdit();
        }
    };

    return (
        <Stack
            sx={{
                background:
                    "linear-gradient(to bottom, transparent 0%, rgba(54, 100, 214, 0.15) 100%)",
                px: 5,
                py: 2,
                mx: -3,
                mb: -2,
                pb: 2,
            }}
        >
            <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                columnGap={1}
            >
                <Stack flex={1}>
                    <Typography
                        fontWeight="bold"
                        color="gray.800"
                        fontSize="14px"
                        lineHeight="20px"
                    >
                        Analysis Goal:
                    </Typography>
                    {isEditing ? (
                        <>
                            <AcxMainTextField
                                id="kbo-analysis-goal-edit"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                multiline
                                rowsMin={3}
                                rowsMax={6}
                                autofocus
                            />
                            <Stack direction="row" gap={1} mt={1}>
                                <AcxButton
                                    color="primary"
                                    variant="text"
                                    onClick={cancelEditing}
                                    sx={{ pl: 0 }}
                                >
                                    Cancel
                                </AcxButton>
                                <AcxButton
                                    color="primary"
                                    variant="contained"
                                    onClick={saveEdit}
                                >
                                    Save
                                    <KeyboardReturn
                                        sx={{ ml: 1 }}
                                        fontSize="small"
                                    />
                                </AcxButton>
                            </Stack>
                        </>
                    ) : (
                        <>
                            <Typography
                                color="gray.700"
                                fontSize="14px"
                                lineHeight="20px"
                                sx={(theme) => ({
                                    maxHeight: theme.spacing(16),
                                    overflowY: "scroll",
                                    ...(!isExpanded && {
                                        display: "-webkit-box",
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                    }),
                                })}
                            >
                                {analysisGoal}
                            </Typography>
                            {isExpanded && (
                                <AcxButton
                                    color="primary"
                                    variant="text"
                                    disabled={isNoLongerSubscribed}
                                    disabledTooltip="You are no longer subscribed to this analysis"
                                    onClick={startEditing}
                                    sx={{ mt: 1, pl: 0 }}
                                >
                                    Edit Prompt
                                    <ArrowForward
                                        sx={{ ml: 1 }}
                                        fontSize="small"
                                    />
                                </AcxButton>
                            )}
                        </>
                    )}
                </Stack>

                {!isEditing && (
                    <IconButton onClick={() => setIsExpanded((v) => !v)}>
                        <ChevronLeft
                            sx={{
                                rotate: isExpanded ? "90deg" : "0deg",
                            }}
                        />
                    </IconButton>
                )}
            </Stack>
        </Stack>
    );
});
